import { cn } from 'lib/cn';
import { Badge } from 'components/ui';
import { ImageIcon, EditIcon } from 'components/icons';
import { formatMoney, humanizeEnum } from 'lib/formatters';
import { StockBadge } from './StockBadge';

/** A single catalog product tile. Clickable when `onClick` is provided (edit). */
export function CatalogItemCard({ item, onClick }) {
  const interactive = typeof onClick === 'function';
  const name = item.vendorProductName || item.name || 'Unnamed product';
  const image = item.vendorPrimaryImageUrlThumbnail || item.vendorPrimaryImageUrl;
  const priceAmount = item.vendorPrice?.amount;
  const showMrp =
    item.mrp != null && priceAmount != null && Number(item.mrp) > Number(priceAmount);

  return (
    <div
      className={cn(
        'panel flex flex-col overflow-hidden',
        interactive && 'cursor-pointer transition hover:border-brand-300 hover:shadow-md',
      )}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <div className="relative aspect-square w-full bg-surface-100">
        {image ? (
          <img src={image} alt={name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300">
            <ImageIcon className="h-10 w-10" />
          </div>
        )}
        {interactive && (
          <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow-sm">
            <EditIcon className="h-4 w-4" />
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <div>
          <p className="line-clamp-1 font-medium text-slate-900">{name}</p>
          {item.brand && <p className="line-clamp-1 text-xs text-slate-500">{item.brand}</p>}
        </div>

        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-slate-900">{formatMoney(item.vendorPrice)}</span>
          {showMrp && (
            <span className="text-xs text-slate-400 line-through">
              {formatMoney(Number(item.mrp))}
            </span>
          )}
        </div>

        <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
          <StockBadge stockQuantity={item.stockQuantity} lowStockThreshold={item.lowStockThreshold} />
          {item.underReview && <Badge tone="info">Under review</Badge>}
          {item.isMisfit && <Badge tone="warning">Needs attention</Badge>}
        </div>

        {(item.subcategory || item.category) && (
          <p className="text-xs text-slate-400">{humanizeEnum(item.subcategory || item.category)}</p>
        )}
      </div>
    </div>
  );
}

export default CatalogItemCard;
